// @ts-nocheck — ported 1:1 from React Native provisioning-cli; runtime-validated
/**
 * WatchGuard Fireware OS CLI Generator
 * 
 * This generator produces CLI configuration scripts for WatchGuard firewalls
 * running Fireware OS. The syntax follows Fireware CLI conventions.
 * 
 * Phase 1: Initial Setup (Block 1)
 * - System name configuration
 * - Basic global settings
 * 
 * Phase 2: Core Networking (Blocks 1-6)
 * - DNS Configuration
 * - WAN Interfaces (external type)
 * - LACP Aggregates (aggX trunks)
 * - VLANs & LAN Interfaces (trusted/optional)
 * - Address Objects (Aliases) & DHCP Servers
 * - Management Interface & Basic Policies
 * 
 * Phase 3: Application Control (Blocks 5.5, 7)
 * - FQDN Aliases for Cloud Services
 * - VLAN-to-App Mapping Policies
 * - Zero-Trust IoT Configuration
 */

import { LACPAggregate, WANConfig, WebFilterConfig, OutboundServicesConfig, CloudServicesMappingConfig, ManagementNetworkConfig, SmartPoliciesConfig } from '../state/wizardStore';
import { CLOUD_SERVICES_FQDN } from './zyxel';
import { IOT_FQDN_GROUPS, ALL_IOT_FQDNS, IOT_TCP_PORT_RANGES, IOT_UDP_PORT_RANGES, getIoTVlans } from '../utils/iotCloudConfig';
import { getModelByName, HardwareModel } from '../hardware/hardwareDictionary';

// ============================================
// SMART POLICIES CONSTANTS
// ============================================

// VLAN Keyword Matching for Smart Policy Resolution
// Updated to match the new Opinionated UI Preset names (case-insensitive)
const VLAN_KEYWORDS = {
  // 'management' preset: Network device management
  admin: ['management', 'admin', 'amministrazione', 'mgmt'],
  
  // 'staff' preset: Employee workstations
  staff: ['staff', 'client', 'dipendenti', 'ufficio', 'office', 'corporate'],
  
  // 'guest' preset: Visitor internet access
  guest: ['guest', 'ospiti', 'visitatori', 'hotspot'],
  
  // 'printers' preset: Network printers (note: preset is 'printers' plural)
  printer: ['printers', 'printer', 'print', 'stampanti', 'stampa', 'mfp'],
  
  // 'voip' preset: IP phones
  voip: ['voip', 'telefoni', 'phone', 'sip', 'pbx'],
  
  // 'iot' preset: Smart devices
  iot: ['iot', 'domotica', 'smart', 'automation', 'cameras'],
  
  // 'ot' preset: HVAC, BMS, SCADA
  ot: ['ot', 'hvac', 'bms', 'scada', 'industrial', 'plc', 'factory'],
  
  // 'servers' preset: On-premises servers
  servers: ['servers', 'server', 'datacenter', 'vm'],
  
  // 'dmz' preset: Public-facing services
  dmz: ['dmz', 'web', 'public'],
  
  // 'blackhole' preset: Quarantine
  blackhole: ['blackhole', 'quarantine', 'blocked'],
};

// ============================================
// SECURITY-FIRST: Interface Access Control Tiers
// ============================================
type VLANSecurityTierWG = 'management' | 'trusted' | 'untrusted';

/**
 * Determines the security tier for a VLAN based on its type/usage
 */
function getVlanSecurityTierWG(vlanType?: string, vlanName?: string): VLANSecurityTierWG {
  const nameLower = (vlanName || '').toLowerCase();
  const typeLower = (vlanType || '').toLowerCase();
  
  // TIER 1 - Management: Full access
  if (typeLower === 'management' || 
      nameLower.includes('management') || 
      nameLower.includes('mgmt') ||
      nameLower.includes('admin')) {
    return 'management';
  }
  
  // TIER 2 - Trusted: Limited access (ping only)
  if (typeLower === 'corporate' || 
      typeLower === 'server' ||
      nameLower.includes('staff') ||
      nameLower.includes('server') ||
      nameLower.includes('voip') ||
      nameLower.includes('dmz')) {
    return 'trusted';
  }
  
  // TIER 3 - Untrusted: NO ACCESS
  return 'untrusted';
}

/**
 * Returns the WatchGuard interface type based on security tier
 */
function getWatchGuardInterfaceType(tier: VLANSecurityTierWG): string {
  switch (tier) {
    case 'management':
      return 'trusted';  // Full management access
    case 'trusted':
      return 'optional';  // Limited access
    case 'untrusted':
      return 'optional';  // Optional but with no management access
  }
}

/**
 * Returns the WatchGuard management access string based on security tier
 */
function getWatchGuardManagement(tier: VLANSecurityTierWG, isNativeManagement: boolean = false): string {
  if (isNativeManagement) {
    return '  management-access https ssh web ping\n';
  }
  
  switch (tier) {
    case 'management':
      return '  management-access https ssh ping\n';
    case 'trusted':
      return '  management-access ping\n';
    case 'untrusted':
      // NO ACCESS - omit the line
      return '';
  }
}

/**
 * Find VLANs matching specific keywords in their names
 */
function findVlansByKeywords(vlans: VLANInput[], keywords: string[]): VLANInput[] {
  return vlans.filter(vlan => {
    const nameLower = vlan.nome_vlan.toLowerCase();
    return keywords.some(kw => nameLower.includes(kw));
  });
}

/**
 * Find first VLAN matching specific keywords
 */
function findVlanByKeywords(vlans: VLANInput[], keywords: string[]): VLANInput | undefined {
  return vlans.find(vlan => {
    const nameLower = vlan.nome_vlan.toLowerCase();
    return keywords.some(kw => nameLower.includes(kw));
  });
}

interface VLANInput {
  nome_vlan: string;
  vlan_id: string;
  network_ip: string;
  gateway_ip: string;
  subnet_mask: string;
  enable_dhcp: boolean;
  dhcp_start?: string;
  dhcp_end?: string;
  dns_primary?: string;
  dns_secondary?: string;
  porte_assegnate: string[];
  vlan_type?: 'corporate' | 'guest' | 'server' | 'management' | 'iot';
}

interface WatchGuardGeneratorInput {
  hostname: string;
  timezone_id: string;
  password_admin: string;
  dns_primary: string;
  dns_secondary: string;
  modello: string;
  
  // Management Port
  mgmt_port?: string;
  mgmt_ip?: string;
  mgmt_subnet?: string;
  
  // LACP Aggregates
  lacp_aggregates?: LACPAggregate[];
  
  // Native Management Network
  managementNetwork?: ManagementNetworkConfig;
  
  // Policies
  policies: string[];
  policy_config: {
    ad_source_vlan?: string;
    ad_dest_vlan?: string;
    guest_vlan?: string;
    corporate_vlans?: string[];
  };
  
  // WebFilter & UTM (Future Phase 3)
  webfilter_config?: WebFilterConfig;
  outbound_services?: OutboundServicesConfig;
  cloud_services_mapping?: CloudServicesMappingConfig;
  
  // Smart Policies Configuration
  smart_policies?: SmartPoliciesConfig;
  
  // VLANs
  vlans: VLANInput[];
  
  // WAN Configuration
  wan1?: WANConfig;
  wan2?: WANConfig;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert subnet mask (e.g., 255.255.255.0) to CIDR notation (e.g., /24)
 */
function subnetMaskToCIDR(mask: string): number {
  const maskMap: Record<string, number> = {
    '255.255.255.255': 32,
    '255.255.255.254': 31,
    '255.255.255.252': 30,
    '255.255.255.248': 29,
    '255.255.255.240': 28,
    '255.255.255.224': 27,
    '255.255.255.192': 26,
    '255.255.255.128': 25,
    '255.255.255.0': 24,
    '255.255.254.0': 23,
    '255.255.252.0': 22,
    '255.255.248.0': 21,
    '255.255.240.0': 20,
    '255.255.224.0': 19,
    '255.255.192.0': 18,
    '255.255.128.0': 17,
    '255.255.0.0': 16,
    '255.254.0.0': 15,
    '255.252.0.0': 14,
    '255.248.0.0': 13,
    '255.240.0.0': 12,
    '255.224.0.0': 11,
    '255.192.0.0': 10,
    '255.128.0.0': 9,
    '255.0.0.0': 8,
  };
  return maskMap[mask] || 24; // Default to /24 if not found
}

/**
 * Map VLAN type to WatchGuard interface type
 * Corporate/Server -> trusted
 * Guest -> optional
 * Management -> custom
 * IoT -> optional (isolated)
 */
function getFirewareInterfaceType(vlanType?: string): string {
  switch (vlanType) {
    case 'corporate':
    case 'server':
      return 'trusted';
    case 'guest':
    case 'iot':
      return 'optional';
    case 'management':
      return 'custom';
    default:
      return 'trusted'; // Default to trusted for internal networks
  }
}

/**
 * Calculate network address from gateway IP and subnet mask
 */
function calculateNetworkAddress(gatewayIP: string, subnetMask: string): string {
  const ipParts = gatewayIP.split('.').map(Number);
  const maskParts = subnetMask.split('.').map(Number);
  const networkParts = ipParts.map((ip, i) => ip & maskParts[i]);
  return networkParts.join('.');
}

/**
 * Generate IoT Zero-Trust Block for WatchGuard Fireware
 */
function generateWatchGuardIoTBlock(vlans: VLANInput[], outbound_services?: OutboundServicesConfig): string {
  // Check if IoT cloud services are enabled
  if (!outbound_services?.iot_cloud) {
    return '';
  }
  
  // Get IoT VLANs
  const iotVlans = vlans.filter(v => v.vlan_type === 'iot');
  if (iotVlans.length === 0) {
    return '';
  }
  
  let script = '';
  
  script += '! ============================================\n';
  script += '! BLOCK IOT: Zero-Trust IoT/Domotics Configuration\n';
  script += '! ============================================\n\n';
  
  // --- FQDN Aliases for IoT ---
  script += '! --- IoT FQDN Aliases ---\n';
  let fqdnIndex = 0;
  Object.entries(IOT_FQDN_GROUPS).forEach(([category, fqdns]) => {
    fqdns.forEach(fqdn => {
      fqdnIndex++;
      const aliasName = `IOT_FQDN_${String(fqdnIndex).padStart(3, '0')}`;
      script += `alias "${aliasName}"\n`;
      script += `  add fqdn ${fqdn}\n`;
      script += `  description "${category.toUpperCase()}"\n`;
      script += 'exit\n';
    });
  });
  script += '\n';
  
  // --- IoT FQDN Group ---
  script += '! --- IoT FQDN Group ---\n';
  script += 'alias "GRP_FQDN_IOT"\n';
  for (let i = 1; i <= fqdnIndex; i++) {
    script += `  add alias "IOT_FQDN_${String(i).padStart(3, '0')}"\n`;
  }
  script += '  description "IoT Smart Home Cloud Endpoints"\n';
  script += 'exit\n\n';
  
  // --- IoT VLAN Subnet Aliases ---
  script += '! --- IoT VLAN Subnet Aliases ---\n';
  iotVlans.forEach(vlan => {
    const networkAddr = calculateNetworkAddress(vlan.gateway_ip, vlan.subnet_mask);
    const cidr = subnetMaskToCIDR(vlan.subnet_mask);
    script += `alias "NET_IOT_${vlan.vlan_id}"\n`;
    script += `  add ipv4 ${networkAddr}/${cidr}\n`;
    script += `  description "IoT VLAN ${vlan.vlan_id} - ${vlan.nome_vlan}"\n`;
    script += 'exit\n';
  });
  script += '\n';
  
  // --- IoT Networks Group ---
  script += '! --- IoT Networks Group ---\n';
  script += 'alias "GRP_NET_IOT"\n';
  iotVlans.forEach(vlan => {
    script += `  add alias "NET_IOT_${vlan.vlan_id}"\n`;
  });
  script += '  description "All IoT VLAN Subnets"\n';
  script += 'exit\n\n';
  
  // --- Service Aliases for IoT Ports ---
  script += '! --- IoT Service Aliases (TCP) ---\n';
  IOT_TCP_PORT_RANGES.forEach((portRange) => {
    const ports = portRange.split('-');
    const serviceName = `IOT_TCP_${portRange.replace('-', '_')}`;
    script += `service-object "${serviceName}"\n`;
    script += `  protocol tcp\n`;
    if (ports[1]) {
      script += `  port-range ${ports[0]} ${ports[1]}\n`;
    } else {
      script += `  port ${ports[0]}\n`;
    }
    script += 'exit\n';
  });
  script += '\n';
  
  script += '! --- IoT Service Aliases (UDP) ---\n';
  IOT_UDP_PORT_RANGES.forEach((portRange) => {
    const ports = portRange.split('-');
    const serviceName = `IOT_UDP_${portRange.replace('-', '_')}`;
    script += `service-object "${serviceName}"\n`;
    script += `  protocol udp\n`;
    if (ports[1]) {
      script += `  port-range ${ports[0]} ${ports[1]}\n`;
    } else {
      script += `  port ${ports[0]}\n`;
    }
    script += 'exit\n';
  });
  script += '\n';
  
  // --- IoT Service Group ---
  script += '! --- IoT Service Group ---\n';
  script += 'service-group "GRP_SRV_IOT"\n';
  IOT_TCP_PORT_RANGES.forEach(p => script += `  add "IOT_TCP_${p.replace('-', '_')}"\n`);
  IOT_UDP_PORT_RANGES.forEach(p => script += `  add "IOT_UDP_${p.replace('-', '_')}"\n`);
  script += '  description "IoT Smart Home Allowed Ports"\n';
  script += 'exit\n\n';
  
  // --- Firewall Policy: IoT to Internet (Restricted) ---
  script += '! --- IoT Firewall Policy (Zero-Trust) ---\n';
  script += 'policy "IOT_TO_INTERNET"\n';
  script += '  action allow\n';
  script += '  from "GRP_NET_IOT"\n';
  script += '  to "GRP_FQDN_IOT"\n';
  script += '  service "GRP_SRV_IOT"\n';
  script += '  nat enable\n';
  script += '  log allow\n';
  script += '  description "Zero-Trust IoT: Allow only authorized cloud services"\n';
  script += 'exit\n\n';
  
  // --- Block Policy: IoT to Internal Networks ---
  const nonIotVlans = vlans.filter(v => v.vlan_type !== 'iot');
  if (nonIotVlans.length > 0) {
    script += '! --- Block IoT to Internal Networks ---\n';
    script += 'policy "BLOCK_IOT_TO_INTERNAL"\n';
    script += '  action deny\n';
    script += '  from "GRP_NET_IOT"\n';
    script += '  to "Any-Trusted"\n';
    script += '  service "Any"\n';
    script += '  log enable\n';
    script += '  description "Zero-Trust: Block IoT from reaching internal VLANs"\n';
    script += 'exit\n\n';
  }
  
  return script;
}

/**
 * Generate WatchGuard Fireware CLI Script
 * 
 * Implements:
 * - Block 1: Initial System Setup + DNS
 * - Block 2: WAN Interfaces (external type)
 * - Block 3: LACP Aggregates (aggX trunks)
 * - Block 4: VLANs & LAN Interfaces
 * - Block 5: Address Objects (Aliases) & DHCP Servers
 * - Block 6: Management Interface & Basic Policies
 * - Block IOT: Zero-Trust IoT Configuration
 * 
 * Future Phase 3:
 * - Application Control (FQDN-based policies)
 */
export const generateWatchGuardCLIScript = (input: WatchGuardGeneratorInput): string => {
  let script = '';
  
  // ============================================
  // HEADER
  // ============================================
  script += '! ============================================\n';
  script += '! WatchGuard Fireware CLI Script\n';
  script += `! Model: ${input.modello}\n`;
  script += `! Hostname: ${input.hostname}\n`;
  script += '! ============================================\n\n';
  
  script += 'configure\n\n';
  
  // ============================================
  // BLOCK 0: Factory Default Cleanup (HAL Dynamic)
  // ============================================
  // WatchGuard Fireboxes boot with factory defaults that MUST be cleared:
  // - eth0: External (WAN) with DHCP client
  // - eth1, eth2, ...: Trusted/Optional with static IPs (10.0.x.1/24) and DHCP servers
  // These defaults conflict with custom deployments and must be purged.
  
  const hardwareModel = getModelByName(input.modello);
  
  // Get factory DHCP ports from HAL, fallback to sensible defaults
  const factoryDhcpPorts: string[] = (hardwareModel as any)?.factoryDhcpPorts || ['eth1', 'eth2'];
  
  script += '! ============================================\n';
  script += '! BLOCK 0: FACTORY DEFAULT CLEANUP (HAL Dynamic)\n';
  script += '! ============================================\n';
  script += `! Model: ${hardwareModel?.displayName || input.modello}\n`;
  script += `! Factory DHCP Ports: ${factoryDhcpPorts.join(', ')}\n`;
  script += '! CRITICAL: Must disable factory DHCP servers and clear default IPs\n';
  script += '! to prevent IP conflicts with custom VLAN assignments.\n';
  script += '! ============================================\n\n';
  
  // Step 1: Disable DHCP servers on all factory DHCP ports
  script += '! Step 1: Disable factory DHCP servers\n';
  for (const port of factoryDhcpPorts) {
    script += `network dhcp-server ${port}\n`;
    script += '  disable\n';
    script += 'exit\n';
  }
  script += '\n';
  
  // Step 2: Clear factory IPs and reset ports to custom type
  script += '! Step 2: Clear factory IPs and reset ports for custom assignment\n';
  for (const port of factoryDhcpPorts) {
    script += `network interface ${port}\n`;
    script += '  type custom\n';
    script += '  no ipv4\n';  // Clear any factory IP (e.g., 10.0.1.1/24)
    script += '  no management\n';  // Remove management access
    script += `  description "Port ${port} - Cleared for custom config"\n`;
    script += 'exit\n';
  }
  script += '\n';
  
  // If LACP is configured, also reset those ports
  if (input.lacp_aggregates && input.lacp_aggregates.length > 0) {
    script += '! Step 3: Prepare LACP member ports for aggregation\n';
    input.lacp_aggregates.forEach((lacp) => {
      lacp.member_ports.forEach((port) => {
        // Only reset if not already in factoryDhcpPorts (avoid duplicate)
        if (!factoryDhcpPorts.includes(port)) {
          script += `network interface ${port}\n`;
          script += '  type custom\n';
          script += '  no ipv4\n';
          script += `  description "LACP member for ${lacp.name}"\n`;
          script += 'exit\n';
        }
      });
    });
    script += '\n';
  }
  
  // ============================================
  // BLOCK 1: Initial Setup + DNS
  // ============================================
  script += '! --- BLOCK 1: INITIAL SETUP & DNS ---\n';
  
  script += 'global-setting\n';
  script += `  system-name ${input.hostname}\n`;
  script += 'exit\n\n';
  
  // DNS Configuration
  if (input.dns_primary || input.dns_secondary) {
    script += 'network dns\n';
    if (input.dns_primary) {
      script += `  add ${input.dns_primary}\n`;
    }
    if (input.dns_secondary) {
      script += `  add ${input.dns_secondary}\n`;
    }
    script += 'exit\n\n';
  }
  
  // ============================================
  // BLOCK 2: WAN Interfaces
  // ============================================
  const hasWan1 = input.wan1?.enabled;
  const hasWan2 = input.wan2?.enabled;
  
  if (hasWan1 || hasWan2) {
    script += '! --- BLOCK 2: WAN CONFIGURATION ---\n';
    
    // Helper function to generate WAN config
    const generateWanConfig = (wan: WANConfig, wanName: string, isDefault: boolean) => {
      const basePort = wan.physical_port || 'eth0';
      const wanType = wan.type;
      
      // Check if WAN is on a tagged VLAN
      if (wan.is_vlan && wan.vlan_id) {
        // WAN on tagged VLAN - create VLAN interface as external
        script += `! ${wanName} - Tagged VLAN ${wan.vlan_id} on ${basePort}\n`;
        script += `network vlan ${wan.vlan_id}\n`;
        script += `  name "${wanName}_VLAN"\n`;
        script += '  type external\n';
        
        if (wanType === 'dhcp') {
          script += isDefault ? '  ipv4 auto default-route\n' : '  ipv4 auto\n';
        } else if (wanType === 'static') {
          if (wan.ip && wan.subnet && wan.gateway) {
            script += isDefault 
              ? `  ipv4 static ${wan.ip} ${wan.subnet} default-route ${wan.gateway}\n`
              : `  ipv4 static ${wan.ip} ${wan.subnet} gateway ${wan.gateway}\n`;
          }
        } else if (wanType === 'pppoe') {
          script += '  pppoe enable\n';
          if (wan.username) script += `  pppoe username "${wan.username}"\n`;
          if (wan.password) script += `  pppoe password "${wan.password}"\n`;
          script += isDefault ? '  ipv4 auto default-route\n' : '  ipv4 auto\n';
        }
        
        script += 'exit\n\n';
        
        // Tag the WAN VLAN on the base interface
        script += `network interface ${basePort}\n`;
        script += `  vlan add ${wan.vlan_id} tagged\n`;
        script += 'exit\n\n';
        
      } else {
        // WAN on native/untagged physical interface
        script += `network interface ${basePort}\n`;
        script += '  type external\n';
        script += `  description "${wanName}"\n`;
        
        if (wanType === 'dhcp') {
          script += isDefault ? '  ipv4 auto default-route\n' : '  ipv4 auto\n';
        } else if (wanType === 'static') {
          if (wan.ip && wan.subnet && wan.gateway) {
            script += isDefault 
              ? `  ipv4 static ${wan.ip} ${wan.subnet} default-route ${wan.gateway}\n`
              : `  ipv4 static ${wan.ip} ${wan.subnet} gateway ${wan.gateway}\n`;
          }
        } else if (wanType === 'pppoe') {
          script += '  pppoe enable\n';
          if (wan.username) script += `  pppoe username "${wan.username}"\n`;
          if (wan.password) script += `  pppoe password "${wan.password}"\n`;
          script += isDefault ? '  ipv4 auto default-route\n' : '  ipv4 auto\n';
        }
        
        script += 'exit\n\n';
      }
    };
    
    // WAN1 Configuration (Primary - default route)
    if (hasWan1 && input.wan1) {
      generateWanConfig(input.wan1, 'Primary WAN', true);
    }
    
    // WAN2 Configuration (Secondary - no default route)
    if (hasWan2 && input.wan2) {
      generateWanConfig(input.wan2, 'Secondary WAN', false);
    }
    
    // Multi-WAN Load Balancing (if both WANs are enabled)
    if (hasWan1 && hasWan2) {
      script += '! Multi-WAN Load Balancing\n';
      script += 'network multi-wan\n';
      script += '  mode load-balance\n';
      script += 'exit\n\n';
    }
  }
  
  // ============================================
  // BLOCK 3: LACP Aggregates (Link Aggregation)
  // ============================================
  if (input.lacp_aggregates && input.lacp_aggregates.length > 0) {
    script += '! --- BLOCK 3: LACP TRUNK CONFIGURATION ---\n';
    
    input.lacp_aggregates.forEach((lacp, index) => {
      const aggName = `agg${index + 1}`;
      
      script += `network interface ${aggName}\n`;
      
      // Set interface type based on management network (only if this aggregate is selected)
      if (input.managementNetwork?.enabled && input.managementNetwork.interface === lacp.name) {
        script += '  type trusted\n';  // Native management = trusted interface
      } else {
        script += '  type custom\n';
      }
      
      script += `  description "${lacp.name}"\n`;
      
      // Add member ports
      lacp.member_ports.forEach((port) => {
        script += `  member ${port}\n`;
      });
      
      // Native Management Network on LACP Parent Interface (only if this aggregate is selected)
      if (input.managementNetwork?.enabled && input.managementNetwork.interface === lacp.name) {
        script += '  ! Native Management Network (Untagged)\n';
        script += `  ipv4 static ${input.managementNetwork.ipAddress} ${input.managementNetwork.subnetMask}\n`;
        script += '  management enable\n';
      } else if (lacp.has_native_ip && lacp.native_ip && lacp.native_subnet) {
        // Legacy: If a native IP is configured on the LACP trunk
        script += `  ipv4 static ${lacp.native_ip} ${lacp.native_subnet}\n`;
      }
      
      script += 'exit\n\n';
    });
    
    // DHCP Server for Native Management Network on LACP parent
    if (input.managementNetwork?.enabled && input.managementNetwork.dhcpEnabled &&
        input.lacp_aggregates.some(l => l.name === input.managementNetwork?.interface)) {
      const aggIndex = input.lacp_aggregates.findIndex(l => l.name === input.managementNetwork?.interface);
      const aggName = `agg${aggIndex + 1}`;
      const mgmtIp = input.managementNetwork.ipAddress;
      const ipParts = mgmtIp.split('.');
      const dhcpStart = input.managementNetwork.dhcpStart || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
      const dhcpEnd = input.managementNetwork.dhcpEnd || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.200`;
      
      script += '! DHCP Server for Native Management Network\n';
      script += `network dhcp-server ${aggName}\n`;
      script += '  enable\n';
      script += `  pool ${dhcpStart} ${dhcpEnd}\n`;
      script += `  default-gateway ${mgmtIp}\n`;
      if (input.dns_primary) {
        script += `  dns ${input.dns_primary}\n`;
      }
      script += 'exit\n\n';
    }
  }
  
  // BLOCK 3b: Native Management Network on Standalone Physical Port
  if (input.managementNetwork?.enabled && input.managementNetwork.interface &&
      !input.lacp_aggregates?.some(l => l.name === input.managementNetwork?.interface)) {
    const portName = input.managementNetwork.interface;
    const mgmtIp = input.managementNetwork.ipAddress;
    const mgmtSubnet = input.managementNetwork.subnetMask;
    
    script += '! ============================================\n';
    script += '! BLOCK 3b: Native Management Network (Standalone Port)\n';
    script += '! ============================================\n\n';
    
    script += `network interface ${portName}\n`;
    script += '  type trusted\n';
    script += '  description "Native Management Network"\n';
    script += `  ipv4 static ${mgmtIp} ${mgmtSubnet}\n`;
    script += '  management enable\n';
    script += 'exit\n\n';
    
    // DHCP Server for Native Management Network on Standalone Port
    if (input.managementNetwork.dhcpEnabled) {
      const ipParts = mgmtIp.split('.');
      const dhcpStart = input.managementNetwork.dhcpStart || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
      const dhcpEnd = input.managementNetwork.dhcpEnd || `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.200`;
      
      script += '! DHCP Server for Native Management Network (Standalone Port)\n';
      script += `network dhcp-server ${portName}\n`;
      script += '  enable\n';
      script += `  pool ${dhcpStart} ${dhcpEnd}\n`;
      script += `  default-gateway ${mgmtIp}\n`;
      if (input.dns_primary) {
        script += `  dns ${input.dns_primary}\n`;
      }
      script += 'exit\n\n';
    }
  }
  
  // Determine the trunk interface name for VLAN tagging
  const getTrunkInterface = (vlan: VLANInput): string => {
    // Check if VLAN is assigned to an LACP aggregate
    if (input.lacp_aggregates && input.lacp_aggregates.length > 0) {
      for (let i = 0; i < input.lacp_aggregates.length; i++) {
        const lacp = input.lacp_aggregates[i];
        // If VLAN ports overlap with LACP members, use the aggregate
        const hasOverlap = vlan.porte_assegnate.some(p => lacp.member_ports.includes(p));
        if (hasOverlap) {
          return `agg${i + 1}`;
        }
      }
    }
    // Otherwise use the first assigned port
    return vlan.porte_assegnate[0] || 'eth2';
  };
  
  // ============================================
  // BLOCK 4: VLANs & LAN Interfaces
  // ============================================
  if (input.vlans && input.vlans.length > 0) {
    script += '! --- BLOCK 4: VLAN INTERFACES ---\n';
    
    input.vlans.forEach((vlan) => {
      // SECURITY-FIRST: Determine access level based on VLAN type
      const securityTier = getVlanSecurityTierWG(vlan.vlan_type, vlan.nome_vlan);
      const interfaceType = getWatchGuardInterfaceType(securityTier);
      const managementLine = getWatchGuardManagement(securityTier, false);
      
      // Define the VLAN globally
      script += `! VLAN: ${vlan.nome_vlan} [Security Tier: ${securityTier.toUpperCase()}]\n`;
      script += `network vlan ${vlan.vlan_id}\n`;
      script += `  name "${vlan.nome_vlan}"\n`;
      script += `  description "${vlan.nome_vlan} Network"\n`;
      script += `  type ${interfaceType}\n`;
      script += `  ipv4 static ${vlan.gateway_ip} ${vlan.subnet_mask}\n`;
      // Only add management access if trusted or management tier
      if (managementLine) {
        script += managementLine;
      } else {
        script += '  ! SECURITY: No management access for untrusted network\n';
      }
      script += 'exit\n\n';
      
      // Tag the VLAN on the trunk interface
      const trunkInterface = getTrunkInterface(vlan);
      script += `! Tag VLAN ${vlan.vlan_id} on trunk\n`;
      script += `network interface ${trunkInterface}\n`;
      script += `  vlan add ${vlan.vlan_id} tagged\n`;
      script += 'exit\n\n';
    });
  }
  
  // ============================================
  // BLOCK 5: Address Objects (Aliases) & DHCP Servers
  // ============================================
  if (input.vlans && input.vlans.length > 0) {
    script += '! --- BLOCK 5: ADDRESS ALIASES & DHCP SERVERS ---\n\n';
    
    // Address Aliases for each VLAN
    script += '! Address Aliases (Network Objects)\n';
    input.vlans.forEach((vlan) => {
      const networkAddr = calculateNetworkAddress(vlan.gateway_ip, vlan.subnet_mask);
      const cidr = subnetMaskToCIDR(vlan.subnet_mask);
      
      script += `alias "${vlan.nome_vlan}_SUBNET"\n`;
      script += `  add ipv4 ${networkAddr}/${cidr}\n`;
      script += 'exit\n\n';
    });
    
    // Group Aliases
    const corporateVlans = input.vlans.filter(v => v.vlan_type === 'corporate' || v.vlan_type === 'server' || !v.vlan_type);
    const guestVlans = input.vlans.filter(v => v.vlan_type === 'guest');
    
    if (corporateVlans.length > 0) {
      script += '! Corporate Networks Group\n';
      script += 'alias "GRP_CORPORATE_NETS"\n';
      corporateVlans.forEach((vlan) => {
        script += `  add alias "${vlan.nome_vlan}_SUBNET"\n`;
      });
      script += 'exit\n\n';
    }
    
    if (guestVlans.length > 0) {
      script += '! Guest Networks Group\n';
      script += 'alias "GRP_GUEST_NETS"\n';
      guestVlans.forEach((vlan) => {
        script += `  add alias "${vlan.nome_vlan}_SUBNET"\n`;
      });
      script += 'exit\n\n';
    }
    
    // DHCP Servers for VLANs with DHCP enabled
    const dhcpVlans = input.vlans.filter(v => v.enable_dhcp);
    
    if (dhcpVlans.length > 0) {
      script += '! DHCP Servers\n';
      dhcpVlans.forEach((vlan) => {
        // Calculate DHCP range if not provided
        let startIP = vlan.dhcp_start;
        let endIP = vlan.dhcp_end;
        
        if (!startIP || !endIP) {
          const ipParts = vlan.gateway_ip.split('.');
          startIP = `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.100`;
          endIP = `${ipParts[0]}.${ipParts[1]}.${ipParts[2]}.250`;
        }
        
        script += `network dhcp-server vlan${vlan.vlan_id}\n`;
        script += '  enable\n';
        script += `  pool ${startIP} ${endIP}\n`;
        script += `  default-gateway ${vlan.gateway_ip}\n`;
        
        // Add DNS servers to DHCP
        if (vlan.dns_primary || input.dns_primary) {
          script += `  dns ${vlan.dns_primary || input.dns_primary}\n`;
        }
        if (vlan.dns_secondary || input.dns_secondary) {
          script += `  dns ${vlan.dns_secondary || input.dns_secondary}\n`;
        }
        
        script += 'exit\n\n';
      });
    }
  }
  
  // ============================================
  // BLOCK 5.5: App FQDN Aliases (Phase 3)
  // ============================================
  // Process cloud_services_mapping to generate FQDN aliases
  if (input.cloud_services_mapping) {
    const servicesWithVlans = Object.entries(input.cloud_services_mapping)
      .filter(([_, vlans]) => vlans && vlans.length > 0)
      .map(([serviceKey]) => serviceKey);
    
    if (servicesWithVlans.length > 0) {
      script += '! --- BLOCK 5.5: APP FQDN ALIASES (Mini-ISDB) ---\n\n';
      
      servicesWithVlans.forEach((serviceKey) => {
        const service = CLOUD_SERVICES_FQDN[serviceKey];
        if (service) {
          script += `! ${service.displayName} FQDN Alias\n`;
          script += `alias "APP_${service.name}"\n`;
          service.fqdns.forEach((fqdn) => {
            script += `  add fqdn ${fqdn}\n`;
          });
          script += 'exit\n\n';
        }
      });
    }
  }
  
  // ============================================
  // BLOCK 6: Management Interface & Basic Policies
  // ============================================
  script += '! --- BLOCK 6: MANAGEMENT & BASIC POLICIES ---\n';
  
  // Management Interface (if configured)
  if (input.mgmt_port && input.mgmt_ip && input.mgmt_subnet) {
    script += `network interface ${input.mgmt_port}\n`;
    script += '  type custom\n';
    script += '  description "Dedicated Management Port"\n';
    script += `  ipv4 static ${input.mgmt_ip} ${input.mgmt_subnet}\n`;
    script += '  management enable\n';
    script += 'exit\n\n';
  }
  
  // ============================================
  // BLOCK 7: App-Specific Policies (Phase 3)
  // ============================================
  // Generate VLAN -> App policies BEFORE general internet rules
  if (input.cloud_services_mapping) {
    const servicesWithVlans = Object.entries(input.cloud_services_mapping)
      .filter(([_, vlans]) => vlans && vlans.length > 0);
    
    if (servicesWithVlans.length > 0) {
      script += '! --- BLOCK 7: APP-SPECIFIC POLICIES (VLAN -> App Mapping) ---\n';
      script += '! These policies allow specific VLANs to access specific Cloud Services\n\n';
      
      servicesWithVlans.forEach(([serviceKey, assignedVlanIds]) => {
        const service = CLOUD_SERVICES_FQDN[serviceKey];
        if (service && assignedVlanIds) {
          // For each VLAN assigned to this service
          assignedVlanIds.forEach((vlanId) => {
            // Find the VLAN details to get its name
            const vlan = input.vlans.find(v => v.vlan_id === vlanId);
            if (vlan) {
              const policyName = `${vlan.nome_vlan}_TO_${service.name}`;
              
              script += `policy "${policyName}"\n`;
              script += '  action allow\n';
              script += `  from "${vlan.nome_vlan}_SUBNET"\n`;
              script += `  to "APP_${service.name}"\n`;
              script += '  service "HTTP" "HTTPS"\n';
              script += '  nat enable\n';
              script += '  log allow\n';
              script += 'exit\n\n';
            }
          });
        }
      });
    }
  }
  
  // ============================================
  // BLOCK 7.5: SMART POLICIES
  // CRITICAL: DROP rules (Priority 1) first, then ALLOW rules (Priority 2)
  // ============================================
  if (input.smart_policies && input.vlans && input.vlans.length > 0) {
    const sp = input.smart_policies;
    
    // Check if any policy is enabled
    const hasAnyPolicy = 
      sp.policy_block_high_risk ||
      sp.policy_isolate_guest ||
      sp.policy_admin_omnipresence ||
      sp.policy_voip_outbound ||
      sp.policy_allow_printers ||
      sp.policy_allow_casting ||
      sp.policy_vendor_ot;
      
    if (hasAnyPolicy) {
      script += '! ============================================\n';
      script += '! BLOCK SMART: Smart Policy Builder Rules\n';
      script += '! ============================================\n\n';
      
      // Find VLANs by keywords
      const adminVlans = findVlansByKeywords(input.vlans, VLAN_KEYWORDS.admin);
      const staffVlans = findVlansByKeywords(input.vlans, VLAN_KEYWORDS.staff);
      const guestVlans = findVlansByKeywords(input.vlans, VLAN_KEYWORDS.guest);
      const printerVlan = findVlanByKeywords(input.vlans, VLAN_KEYWORDS.printer);
      const voipVlans = findVlansByKeywords(input.vlans, VLAN_KEYWORDS.voip);
      const iotVlans = findVlansByKeywords(input.vlans, VLAN_KEYWORDS.iot);
      const otVlan = findVlanByKeywords(input.vlans, VLAN_KEYWORDS.ot);
      
      // DEBUG: Log resolved VLANs
      console.log('[SmartPolicies-WatchGuard] Resolved VLANs:', {
        admin: adminVlans.map(v => v.nome_vlan),
        staff: staffVlans.map(v => v.nome_vlan),
        guest: guestVlans.map(v => v.nome_vlan),
        printer: printerVlan?.nome_vlan,
        voip: voipVlans.map(v => v.nome_vlan),
        iot: iotVlans.map(v => v.nome_vlan),
        ot: otVlan?.nome_vlan,
      });
      
      // --- SERVICE OBJECTS ---
      script += '! --- Smart Policy Service Objects ---\n';
      
      if (sp.policy_allow_printers) {
        script += 'service-object "SVC_PRINTERS"\n';
        script += '  protocol tcp\n';
        script += '  port 9100\n';
        script += '  port 631\n';
        script += '  port 515\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_allow_casting) {
        script += 'service-object "SVC_CASTING_TCP"\n';
        script += '  protocol tcp\n';
        script += '  port-range 7000 7100\n';
        script += '  port-range 8008 8009\n';
        script += 'exit\n';
        script += 'service-object "SVC_CASTING_UDP"\n';
        script += '  protocol udp\n';
        script += '  port 5353\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_voip_outbound) {
        script += 'service-object "SVC_VOIP_SIP"\n';
        script += '  protocol udp\n';
        script += '  port-range 5060 5061\n';
        script += 'exit\n';
        script += 'service-object "SVC_VOIP_RTP"\n';
        script += '  protocol udp\n';
        script += '  port-range 10000 64999\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_block_high_risk) {
        script += 'service-object "SVC_HIGH_RISK"\n';
        script += '  protocol tcp\n';
        script += '  port 445\n';
        script += '  port 3389\n';
        script += '  port 1433\n';
        script += '  port 3306\n';
        script += 'exit\n\n';
      }
      
      if (sp.policy_vendor_ot) {
        script += 'service-object "SVC_OT_HVAC"\n';
        script += '  protocol tcp\n';
        script += '  port 502\n';
        script += 'exit\n';
        script += 'service-object "SVC_BACNET"\n';
        script += '  protocol udp\n';
        script += '  port 47808\n';
        script += 'exit\n\n';
      }
      
      // --- PRIORITY 1: DROP RULES ---
      script += '! --- [P1-DROP] Smart Policy DROP Rules ---\n';
      
      // 1a. Block High-Risk to WAN
      if (sp.policy_block_high_risk) {
        script += 'policy "SP_BLOCK_HIGH_RISK_OUT"\n';
        script += '  action deny\n';
        script += '  from "Any-Trusted"\n';
        script += '  to "Any-External"\n';
        script += '  service "SVC_HIGH_RISK"\n';
        script += '  log enable\n';
        script += '  description "Smart Policy: Block SMB/RDP/SQL egress"\n';
        script += 'exit\n\n';
      }
      
      // 1b. Isolate Guest
      if (sp.policy_isolate_guest && guestVlans.length > 0) {
        guestVlans.forEach(guestVlan => {
          const networkAddr = calculateNetworkAddress(guestVlan.gateway_ip, guestVlan.subnet_mask);
          const cidr = subnetMaskToCIDR(guestVlan.subnet_mask);
          
          script += `policy "SP_ISOLATE_${guestVlan.nome_vlan}"\n`;
          script += '  action deny\n';
          script += `  from "${guestVlan.nome_vlan}_SUBNET"\n`;
          script += '  to "Any-Trusted"\n';
          script += '  service "Any"\n';
          script += '  log enable\n';
          script += '  description "Smart Policy: Guest isolation"\n';
          script += 'exit\n\n';
        });
      }
      
      // --- PRIORITY 2: ALLOW RULES ---
      script += '! --- [P2-ALLOW] Smart Policy ALLOW Rules ---\n';
      
      // 2a. Admin Omnipresence
      if (sp.policy_admin_omnipresence && adminVlans.length > 0) {
        adminVlans.forEach(adminVlan => {
          script += `policy "SP_ADMIN_${adminVlan.nome_vlan}_OMNI"\n`;
          script += '  action allow\n';
          script += `  from "${adminVlan.nome_vlan}_SUBNET"\n`;
          script += '  to "Any"\n';
          script += '  service "Any"\n';
          script += '  log enable\n';
          script += '  description "Smart Policy: Admin full access"\n';
          script += 'exit\n\n';
        });
      }
      
      // 2b. VoIP Outbound
      if (sp.policy_voip_outbound && voipVlans.length > 0) {
        voipVlans.forEach(voipVlan => {
          script += `policy "SP_VOIP_${voipVlan.nome_vlan}_OUT"\n`;
          script += '  action allow\n';
          script += `  from "${voipVlan.nome_vlan}_SUBNET"\n`;
          script += '  to "Any-External"\n';
          script += '  service "SVC_VOIP_SIP" "SVC_VOIP_RTP"\n';
          script += '  nat enable\n';
          script += '  log enable\n';
          script += '  description "Smart Policy: VoIP outbound"\n';
          script += 'exit\n\n';
        });
      }
      
      // 2c. Allow Printers
      if (sp.policy_allow_printers && staffVlans.length > 0 && printerVlan) {
        staffVlans.forEach(staffVlan => {
          script += `policy "SP_${staffVlan.nome_vlan}_TO_PRINT"\n`;
          script += '  action allow\n';
          script += `  from "${staffVlan.nome_vlan}_SUBNET"\n`;
          script += `  to "${printerVlan.nome_vlan}_SUBNET"\n`;
          script += '  service "SVC_PRINTERS"\n';
          script += '  log enable\n';
          script += '  description "Smart Policy: Staff to printers"\n';
          script += 'exit\n\n';
        });
      }
      
      // 2d. Allow Casting
      if (sp.policy_allow_casting && iotVlans.length > 0) {
        const castingSources = [...staffVlans, ...guestVlans];
        if (castingSources.length > 0) {
          iotVlans.forEach(iotVlan => {
            castingSources.forEach(srcVlan => {
              script += `policy "SP_${srcVlan.nome_vlan}_CAST_${iotVlan.nome_vlan}"\n`;
              script += '  action allow\n';
              script += `  from "${srcVlan.nome_vlan}_SUBNET"\n`;
              script += `  to "${iotVlan.nome_vlan}_SUBNET"\n`;
              script += '  service "SVC_CASTING_TCP" "SVC_CASTING_UDP"\n';
              script += '  log enable\n';
              script += '  description "Smart Policy: Casting to IoT"\n';
              script += 'exit\n\n';
            });
          });
        }
      }
      
      // 2e. Admin/Management to OT (replaces Vendor to OT since we don't have Vendor preset)
      if (sp.policy_vendor_ot && otVlan) {
        // Use admin VLANs as source (Management network can access OT)
        if (adminVlans.length > 0) {
          adminVlans.forEach(adminVlan => {
            script += `policy "SP_${adminVlan.nome_vlan}_TO_OT"\n`;
            script += '  action allow\n';
            script += `  from "${adminVlan.nome_vlan}_SUBNET"\n`;
            script += `  to "${otVlan.nome_vlan}_SUBNET"\n`;
            script += '  service "SVC_OT_HVAC" "SVC_BACNET"\n';
            script += '  log enable\n';
            script += '  description "Smart Policy: Admin to OT/HVAC"\n';
            script += 'exit\n\n';
          });
        } else if (staffVlans.length > 0) {
          // Fallback: If no admin VLAN, allow Staff to OT
          staffVlans.forEach(staffVlan => {
            script += `policy "SP_${staffVlan.nome_vlan}_TO_OT"\n`;
            script += '  action allow\n';
            script += `  from "${staffVlan.nome_vlan}_SUBNET"\n`;
            script += `  to "${otVlan.nome_vlan}_SUBNET"\n`;
            script += '  service "SVC_OT_HVAC" "SVC_BACNET"\n';
            script += '  log enable\n';
            script += '  description "Smart Policy: Staff to OT/HVAC"\n';
            script += 'exit\n\n';
          });
        }
      }
    }
  }
  
  // Basic Outbound Policies
  script += '! Basic Firewall Policies\n';
  
  // Corporate Outbound Policy
  const hasCorporateVlans = input.vlans.some(v => v.vlan_type === 'corporate' || v.vlan_type === 'server' || !v.vlan_type);
  
  if (hasCorporateVlans) {
    script += 'policy "CORP_TO_INTERNET"\n';
    script += '  action allow\n';
    script += '  from "Any-Trusted"\n';
    script += '  to "Any-External"\n';
    script += '  service "HTTP" "HTTPS" "DNS"\n';
    
    // Add additional services based on outbound config
    if (input.outbound_services) {
      if (input.outbound_services.imaps) {
        script += '  service "IMAPS"\n';
      }
      if (input.outbound_services.smtps) {
        script += '  service "SMTPS"\n';
      }
      if (input.outbound_services.ftp) {
        script += '  service "FTP"\n';
      }
      if (input.outbound_services.ntp) {
        script += '  service "NTP"\n';
      }
    }
    
    script += '  nat enable\n';
    script += '  log enable\n';
    script += 'exit\n\n';
  }
  
  // Guest Outbound Policy (more restrictive)
  const hasGuestVlans = input.vlans.some(v => v.vlan_type === 'guest');
  
  if (hasGuestVlans) {
    script += 'policy "GUEST_TO_INTERNET"\n';
    script += '  action allow\n';
    script += '  from "Any-Optional"\n';
    script += '  to "Any-External"\n';
    script += '  service "HTTP" "HTTPS" "DNS"\n';
    script += '  nat enable\n';
    script += '  log enable\n';
    script += 'exit\n\n';
    
    // Guest Isolation - Deny guest to internal
    script += '! Guest Network Isolation\n';
    script += 'policy "DENY_GUEST_TO_INTERNAL"\n';
    script += '  action deny\n';
    script += '  from "Any-Optional"\n';
    script += '  to "Any-Trusted"\n';
    script += '  log enable\n';
    script += 'exit\n\n';
  }
  
  // LAN to Server AD Policy (if configured)
  if (input.policies.includes('lan_to_server') && 
      input.policy_config.ad_source_vlan && 
      input.policy_config.ad_dest_vlan) {
    
    // Generate Windows AD Service Policy Template (Fortinet Parity)
    script += '! --- Windows AD Service Policy (Fortinet Parity) ---\n';
    script += '! Windows AD Ports: DCE-RPC(135), LDAP(389), Kerberos(88),\n';
    script += '!                   SMB(445), NetBIOS(137,138,139), DNS(53)\n\n';
    
    script += '! Custom Policy Template for Windows AD Services\n';
    script += 'policy-template "SVC_WINDOWS_AD"\n';
    script += '  service "DCE-RPC"\n';
    script += '  service "LDAP"\n';
    script += '  service "LDAPS"\n';
    script += '  service "Kerberos"\n';
    script += '  service "SMB"\n';
    script += '  service "NetBIOS-NS"\n';
    script += '  service "NetBIOS-DGM"\n';
    script += '  service "NetBIOS-SSN"\n';
    script += '  service "DNS"\n';
    script += '  service "RDP"\n';
    script += 'exit\n\n';
    
    script += '! LAN to AD Server Policy\n';
    script += 'policy "LAN_TO_AD_SERVER"\n';
    script += '  action allow\n';
    script += `  from "${input.policy_config.ad_source_vlan}_SUBNET"\n`;
    script += `  to "${input.policy_config.ad_dest_vlan}_SUBNET"\n`;
    script += '  ! Windows AD Services: DCE-RPC, LDAP, Kerberos, SMB, NetBIOS, DNS, RDP\n';
    script += '  service "DCE-RPC" "LDAP" "LDAPS" "Kerberos" "SMB"\n';
    script += '  service "NetBIOS-NS" "NetBIOS-DGM" "NetBIOS-SSN" "DNS" "RDP"\n';
    script += '  nat disable\n';
    script += '  log enable\n';
    script += 'exit\n\n';
  }
  
  // ============================================
  // BLOCK IOT: Zero-Trust IoT Configuration
  // ============================================
  script += generateWatchGuardIoTBlock(input.vlans, input.outbound_services);
  
  // ============================================
  // END OF CONFIGURATION
  // ============================================
  script += '! ============================================\n';
  script += '! END OF CONFIGURATION - All Phases Complete\n';
  script += '! ============================================\n';
  script += '!\n';
  script += '! Phase 1: Initial Setup & DNS\n';
  script += '! Phase 2: WAN, LACP, VLANs, DHCP, Basic Policies\n';
  script += '! Phase 3: App FQDN Aliases & VLAN->App Policies\n';
  script += '! Phase 4: Zero-Trust IoT Configuration\n';
  script += '! ============================================\n';
  
  return script;
};
